import React, {useRef, useState} from 'react';
import {
  Box,
  Checkbox,
  TableRow,
  TableCell,
  tableCellClasses,
  TableHead,
  TableSortLabel,
  tableSortLabelClasses,
  useTheme,
  OutlinedInput,
  TextField,
  FormControl,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Close} from '@mui/icons-material';
import {debounce} from 'lodash';
import {useTranslation} from 'react-i18next';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker as MUIDatePicker} from '@mui/x-date-pickers/DatePicker';
import {localToUTC, makeLocalAppearUTC} from '../../Form/_partials/InputDatePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

const DateInput = ({onChange}: any) => {
  const [value, setValue] = useState<any>(null);

  return (
    <FormControl sx={{bgcolor: 'white', height: 35, py: 0, border: 'none'}} fullWidth>
      <LocalizationProvider sx={{width: '100%', py: 0, bgcolor: 'white'}} dateAdapter={AdapterDayjs}>
        <MUIDatePicker
          InputProps={{
            sx: {
              border: 'none',
              outline: 'none',
              borderRadius: 0,
              py: '0 !important',
              height: 33,
              flex: 1,
              '&:hover': {
                outline: 'none',
                border: 'none',
              },
            },
          }}
          inputFormat="DD/MM/YYYY"
          renderInput={params => <TextField sx={{height: 35}} size={'small'} {...params} />}
          value={value ? makeLocalAppearUTC(value) : null}
          onChange={(value: any) => {
            const date = value?.$d ? localToUTC(value?.$d) : '';
            setValue(date);
            onChange(date);
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );
};

const StyledTableCell = styled(TableCell)(({theme}) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    border: '0.01em solid rgba(241, 243, 244, 1)!important',
    fontSize: 14,
  },
}));

const SearchStyle = styled(OutlinedInput)(({theme}: any) => ({
  width: '100%',
  borderRadius: 0,
  fontSize: 12,
}));

const StyledTableSortLabel = styled(TableSortLabel)(({theme}) => ({
  [`&.${tableSortLabelClasses.active}`]: {
    color: theme.palette.common.white,
  },
  [`& .${tableSortLabelClasses.icon}`]: {
    color: `${theme.palette.common.white}!important`,
  },
}));

const TableHeaderCell = ({headCell, setDragItem, orderBy, order, createSortHandler, groupable}: any) => {
  return (
    <StyledTableCell
      onDragStart={(e: any) => {
        setDragItem(e.target.id);
      }}
      id={headCell.id}
      draggable={headCell.id && groupable ? true : false}
      key={headCell.id}
      align={headCell.numeric || headCell.alignRight ? 'right' : 'left'}
      padding={headCell.disablePadding ? 'none' : 'normal'}
      sortDirection={orderBy === headCell.id ? order : false}
      width={headCell.width}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <StyledTableSortLabel
          hideSortIcon
          active={orderBy === headCell.id}
          direction={orderBy === headCell.id ? order : 'asc'}
          onClick={() => createSortHandler(headCell.id)}
          sx={{whiteSpace: 'nowrap'}}
        >
          {headCell.label}
          {orderBy === headCell.id ? (
            <Box sx={{...visuallyHidden}}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>
          ) : null}
        </StyledTableSortLabel>
      </Box>
    </StyledTableCell>
  );
};

const TableHeaderFilterCell = ({headCell, onFilterByColumn}: any) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const [isFiltered, setIsFiltered] = useState(false);

  const inputRef = useRef<any>(null);

  const onFilter = debounce(text => {
    onFilterByColumn(text, headCell.id);
    setIsFiltered(text?.length !== 0);
    //inputRef.current.value = text;
  }, 250);

  const isFilterDisable = headCell.filter === false || headCell.id === '';

  const getElement = type => {
    switch (type) {
      case 'date':
        return (
          <DateInput
            onChange={(date: any) => {
              const string = moment(date).format('YYYY-MM-DD');
              onFilter(string !== 'Invalid date' ? string : '');
            }}
          />
        );

      default:
        return (
          <SearchStyle
            //inputRef={inputRef}
            onChange={e => {
              onFilter(e.target.value);
            }}
            placeholder={isFilterDisable ? '' : t('search') || ''}
            sx={{
              bgcolor: isFilterDisable ? theme.palette.grey[300] : theme.palette.common.white,
              py: 0,
              height: 35,
              flex: 1,
            }}
            readOnly={isFilterDisable}
            disabled={isFilterDisable}
            endAdornment={
              <>
                {isFiltered && (
                  <Close
                    onClick={() => {
                      onFilter('');
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: theme.palette.grey[400],
                      width: 20,
                      height: 20,
                    }}
                  />
                )}
              </>
            }
          />
        );
    }
  };

  return (
    <StyledTableCell id={headCell.id} key={headCell.id} padding={'none'}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      ></Box>
      {getElement(headCell.type)}
    </StyledTableCell>
  );
};

export default function TableHeader(props: any) {
  const {
    order,
    orderBy,
    rowCount,
    headLabel,
    numSelected,
    onRequestSort,
    onSelectAllClick,
    checkbox,
    collapsable,
    setDragItem,
    dragItem,
    groupItems,
    setGroupItems,
    onFilterByColumn,
    groupable,
    disableRowFilter,
  } = props;

  const createSortHandler = (property: any) => {
    onRequestSort(property);
  };

  return (
    <TableHead>
      <TableRow>
        {checkbox && (
          <StyledTableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </StyledTableCell>
        )}
        {collapsable && <StyledTableCell padding="checkbox"></StyledTableCell>}
        {headLabel.map((headCell: any, index: number) => (
          <TableHeaderCell
            key={index}
            disableRowFilter={disableRowFilter}
            groupable={groupable}
            setDragItem={setDragItem}
            dragItem={dragItem}
            groupItems={groupItems}
            setGroupItems={setGroupItems}
            headCell={headCell}
            order={order}
            orderBy={orderBy}
            createSortHandler={createSortHandler}
            onFilterByColumn={onFilterByColumn}
          />
        ))}
      </TableRow>
      {!disableRowFilter && (
        <TableRow>
          {headLabel.map((headCell: any, index: number) => (
            <TableHeaderFilterCell
              key={index + '_filter_input'}
              headCell={headCell}
              onFilterByColumn={onFilterByColumn}
            />
          ))}
        </TableRow>
      )}
    </TableHead>
  );
}
